import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { TasksTabProvider } from "../../../context/TasksContext";
import { useTasksTab } from "../../../hooks/Tasks/useTasksTab";
import { useToast } from "../../../context/ToastContext";
import { CARD_SX, PRIMARY_COLOR } from "../../../components/Tasks/constants";
import { Subject } from "../../../types/CaseTypes";
import WorkspacePanel from "../../../components/Tasks/WorkspacePanel";
import CaseSummaryPanel from "./CaseSummaryPanel";
import ObjectivesView from "./ObjectivesView";
import NewTaskSheet from "../../../components/Tasks/NewTaskSheet";
import PasteTargetsModal from "../../../components/Tasks/PasteTargetsModal";
import TasksTopBar from "../../../components/Tasks/TasksTopBar";
import TaskListPanel from "../../../components/Tasks/TaskListPanel";
import ActivityTimeline from "../../../components/Tasks/ActivityTimeline";
import { WorkspaceSkeleton } from "../../../components/Tasks/Skeletons";
import { tokens } from "../../../theme/tokens";

const TasksTabInner: React.FC = () => {
  const { tasks, selectedTaskId, setSelectedTaskId, isLoading } = useTasksTab();
  const { showToast } = useToast();

  const history = useHistory();
  const location = useLocation();

  const replaceSearch = (p: URLSearchParams) =>
    history.replace({ search: p.toString() ? `?${p}` : "" });

  const [mainView, setMainViewState] = useState<"tasks" | "objectives">(() =>
    new URLSearchParams(location.search).get("view") === "objectives"
      ? "objectives"
      : "tasks",
  );

  const setMainView = (view: "tasks" | "objectives") => {
    setMainViewState(view);
    const p = new URLSearchParams(window.location.search);
    if (view === "objectives") {
      p.set("view", "objectives");
      p.delete("task");
    } else {
      p.delete("view");
    }
    replaceSearch(p);
  };

  // push (not replace) so back button returns to objectives view
  const jumpToTask = (taskId: string) => {
    setSelectedTaskId(taskId);
    setMainViewState("tasks");
    const p = new URLSearchParams(window.location.search);
    p.delete("view");
    p.set("task", taskId);
    history.push({ search: `?${p}` });
  };

  // Initialize selectedTaskId from URL once tasks have finished loading
  const didInitFromUrl = useRef(false);
  useEffect(() => {
    if (didInitFromUrl.current || isLoading) return;
    didInitFromUrl.current = true;
    const taskId = new URLSearchParams(location.search).get("task");
    if (!taskId) return;
    if (tasks.some((t) => t.task_id === taskId)) {
      setSelectedTaskId(taskId);
    } else {
      showToast("Task not found", "error");
      const p = new URLSearchParams(window.location.search);
      p.delete("task");
      replaceSearch(p);
    }
  }, [tasks, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL → state for browser back / forward navigation
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const urlView: "tasks" | "objectives" =
      p.get("view") === "objectives" ? "objectives" : "tasks";
    const urlTask = p.get("task") ?? null;
    setMainViewState(urlView);
    if (!urlTask) {
      setSelectedTaskId(null);
    } else if (tasks.some((t) => t.task_id === urlTask)) {
      setSelectedTaskId(urlTask);
    } else if (didInitFromUrl.current) {
      showToast("Task not found", "error");
      const cleared = new URLSearchParams(location.search);
      cleared.delete("task");
      replaceSearch(cleared);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedTaskId → URL so TaskListPanel's direct setSelectedTaskId calls update the URL
  // Gated on didInitFromUrl so the initial null value does not strip a deep-link ?task= param
  useEffect(() => {
    if (!didInitFromUrl.current) return;
    const p = new URLSearchParams(window.location.search);
    if (selectedTaskId) {
      p.set("task", selectedTaskId);
    } else {
      p.delete("task");
    }
    replaceSearch(p);
  }, [selectedTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [summaryOpen, setSummaryOpen] = useState(true);

  const toggle = (
    <Box
      sx={{
        display: "flex",
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: "8px",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {(["tasks", "objectives"] as const).map((view, i) => (
        <button
          key={view}
          type="button"
          onClick={() => setMainView(view)}
          style={{
            fontSize: "12px",
            fontWeight: 600,
            padding: "5px 14px",
            border: "none",
            borderRight: i === 0 ? `1px solid ${tokens.color.border.default}` : "none",
            backgroundColor: mainView === view ? PRIMARY_COLOR : tokens.color.surface.default,
            color: mainView === view ? tokens.color.surface.default : tokens.color.text.secondary,
            cursor: "pointer",
            textTransform: "capitalize",
            transition: "all 0.12s ease",
          }}
        >
          {view === "tasks" ? "Tasks" : "Objectives"}
        </button>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 60px)",
        backgroundColor: tokens.color.surface.sunken,
      }}
    >
      <TasksTopBar leftSlot={toggle} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: "12px",
          p: "12px",
          minHeight: 0,
        }}
      >
        {mainView === "objectives" ? (
          <ObjectivesView onJumpToTask={jumpToTask} />
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flex: 1,
              minHeight: 0,
              alignItems: "stretch",
            }}
          >
            <TaskListPanel />
            <Box
              sx={{
                ...CARD_SX,
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoading ? <WorkspaceSkeleton /> : <WorkspacePanel />}
            </Box>
          </Box>
        )}

        <ActivityTimeline />

        {/* Collapsible case summary */}
        <Box sx={{ ...CARD_SX, flexShrink: 0 }}>
          <Box
            onClick={() => setSummaryOpen((o) => !o)}
            sx={{
              px: "18px",
              py: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              borderBottom: summaryOpen ? `1px solid ${tokens.color.border.default}` : "none",
              userSelect: "none",
            }}
          >
            <span
              style={{
                fontSize: "10px",
                fontWeight: 700,
                color: tokens.color.text.secondary,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Case Summary
            </span>
            {summaryOpen ? (
              <ExpandLessIcon sx={{ fontSize: "16px", color: tokens.color.text.secondary }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: "16px", color: tokens.color.text.secondary }} />
            )}
          </Box>
          {summaryOpen && <CaseSummaryPanel />}
        </Box>
      </Box>

      <NewTaskSheet />
      <PasteTargetsModal />
    </Box>
  );
};

interface TasksTabProps {
  caseId: string;
  clientId: string;
  subjects?: Subject[];
  onCreateSubject: (subject: Subject) => Promise<void>;
}

const TasksTab: React.FC<TasksTabProps> = ({
  caseId,
  clientId,
  subjects = [],
  onCreateSubject,
}) => (
  <TasksTabProvider
    caseId={caseId}
    clientId={clientId}
    subjects={subjects}
    onCreateSubject={onCreateSubject}
  >
    <TasksTabInner />
  </TasksTabProvider>
);

export default TasksTab;
